import type {
  DefinitionsFromApi,
  QueryArgFrom,
  QueryKeys,
  ResultTypeFrom,
} from '@reduxjs/toolkit/query';

export interface CachedEndpointEntry<TArgs, TData> {
  originalArgs: TArgs;
  data: TData;
}

/**
 * Structural RTK Query Api surface used for assignability only.
 * Parameter positions use `never` so real createApi instances remain assignable
 * under strict function types; args/data for callers come from DefinitionsFromApi.
 */
type CacheIntrospectionApi = {
  util: {
    selectCachedArgsForQuery: (
      state: never,
      endpointName: never,
    ) => ReadonlyArray<object | string | number | boolean | null | undefined>;
  };
  endpoints: Record<
    string,
    {
      // Intentionally loose — mutations may expose `data?: unknown` from RTK.
      select: (arg: never) => (state: never) => object;
    }
  >;
};

type ApiDefinitions<TApi> = DefinitionsFromApi<TApi>;

type QueryArgsOfApi<
  TApi,
  EndpointName extends QueryKeys<ApiDefinitions<TApi>>,
> = QueryArgFrom<ApiDefinitions<TApi>[EndpointName]>;

type QueryDataOfApi<
  TApi,
  EndpointName extends QueryKeys<ApiDefinitions<TApi>>,
> = ResultTypeFrom<ApiDefinitions<TApi>[EndpointName]>;

/**
 * Enumerates fulfilled RTK Query cache entries for a specific endpoint.
 * `originalArgs` and `data` are inferred from the Api endpoint definition —
 * pass the api instance and a literal endpoint name (no manual type args).
 *
 * Prefer this over parsing `${endpointName}(${JSON.stringify(args)})` cache keys.
 * Built on `api.util.selectCachedArgsForQuery` + endpoint selectors.
 *
 * @example
 * getCachedEntriesForEndpoint(state, holdingsApi, 'getHoldingDetails')
 */
export function getCachedEntriesForEndpoint<
  TApi extends CacheIntrospectionApi,
  const EndpointName extends QueryKeys<ApiDefinitions<TApi>> & string,
>(
  state: object,
  api: TApi,
  endpointName: EndpointName,
): Array<
  CachedEndpointEntry<
    QueryArgsOfApi<TApi, EndpointName>,
    QueryDataOfApi<TApi, EndpointName>
  >
> {
  type Args = QueryArgsOfApi<TApi, EndpointName>;
  type Data = QueryDataOfApi<TApi, EndpointName>;

  const cachedArgs = api.util.selectCachedArgsForQuery(
    state as never,
    endpointName as never,
  ) as ReadonlyArray<Args>;

  const select = api.endpoints[endpointName].select as (arg: Args) => (
    state: object,
  ) => {
    data?: Data;
  };

  const entries: Array<CachedEndpointEntry<Args, Data>> = [];

  for (const originalArgs of cachedArgs) {
    const result = select(originalArgs)(state);
    if (result.data === undefined) {
      continue;
    }
    entries.push({
      originalArgs,
      data: result.data,
    });
  }

  return entries;
}
